import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSync } from '@fortawesome/free-solid-svg-icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import Comments from './Comments/Comments';

interface OrganizationDetails {
  id: number;
  name: string;
  amount: number;
  duns: string;
  uei: string;
  recipient_level: string;
  is_favorited: boolean;
  metadata: {
    location?: {
      address_line1?: string;
      city_name?: string;
      state_code?: string;
      zip?: string;
    };
    alternate_names?: string[];
    business_types?: string[];
  };
  awarding_agency: {
    results: Array<{
      name: string;
      code: string;
      amount: number;
    }>;
  };
  awarding_subagency: {
    results: Array<{
      name: string;
      code: string;
      amount: number;
    }>;
  };
  federal_account: {
    results: Array<{
      name: string;
      code: string;
      amount: number;
    }>;
  };
  cfda: {
    results: Array<{
      name: string;
      code: string;
      amount: number;
    }>;
  };
  psc: {
    results: Array<{
      name: string;
      code: string;
      amount: number;
    }>;
  };
  naics: {
    results: Array<{
      name: string;
      code: string;
      amount: number;
    }>;
  };
}

const fetchOrganization = async (id: string) => {
  const response = await fetch(`/api/usa_spending_recipients/${id}`);
  if (!response.ok) {
    throw new Error('Failed to fetch organization');
  }
  return response.json();
};

const OrganizationDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();

  const {
    data: organization,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['organization', id],
    queryFn: () => fetchOrganization(id!),
    enabled: !!id,
  });

  const { mutate: refreshOrganization, isPending: isRefreshing } = useMutation({
    mutationFn: async () => {
      await fetch(`/api/usa_spending_recipients/${id}/refresh`, {
        method: 'POST',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['organization', id] });
    },
  });

  const { mutate: toggleFavorite } = useMutation({
    mutationFn: async () => {
      const method = organization?.is_favorited ? 'DELETE' : 'POST';
      const response = await fetch(`/api/usa_spending_recipients/${id}/favorite`, {
        method,
      });
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.setQueryData(['organization', id], (oldData: OrganizationDetails | undefined) => {
        if (!oldData) return oldData;
        return { ...oldData, is_favorited: data.is_favorited };
      });
    },
  });

  const handleRefresh = () => {
    refreshOrganization();
  };

  const handleToggleFavorite = () => {
    if (!organization) return;
    toggleFavorite();
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full dark:text-gray-200">
        Loading organization details...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-full dark:text-gray-200">
        Error loading organization details
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="flex justify-center items-center h-full dark:text-gray-200">Organization not found</div>
    );
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  return (
    <div className="h-full flex flex-row">
      {/* Main Content */}
      <div className="flex-1 bg-gray-50 dark:bg-gray-900 p-8 overflow-y-auto">
        <div className="max-w-[calc(100vw-400px)]">
          <div className="mb-6 flex items-center gap-4">
            <Link
              to="/organizations"
              className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 flex items-center gap-2"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              Back to Organizations
            </Link>
            <h1 className="text-2xl font-bold dark:text-white">{organization?.name}</h1>
            <div className="ml-auto flex items-center gap-2">
              <a
                href={`/admin/usa_spending_recipients/${organization?.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                <i className="fa-solid fa-shield-alt" />
                Admin
              </a>
              <button
                onClick={handleToggleFavorite}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                <i
                  className={`fa-${organization?.is_favorited ? 'solid' : 'regular'} fa-star text-yellow-500`}
                />
                {organization?.is_favorited ? 'Favorited' : 'Favorite'}
              </button>
              <button
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed"
              >
                <FontAwesomeIcon icon={faSync} spin={isRefreshing} />
                {isRefreshing ? 'Refreshing...' : 'Refresh'}
              </button>
            </div>
          </div>

          {/* Overview Card */}
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg mb-6">
            <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-medium text-gray-900 dark:text-white">Recipient Overview</h3>
            </div>
            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Total Amount</dt>
                    <dd className="text-lg text-gray-900 dark:text-gray-100">
                      {organization?.amount ? formatCurrency(organization.amount) : 'N/A'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">DUNS</dt>
                    <dd className="text-gray-900 dark:text-gray-100">{organization.duns || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">UEI</dt>
                    <dd className="text-gray-900 dark:text-gray-100">{organization.uei || 'N/A'}</dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Recipient Level</dt>
                    <dd className="text-gray-900 dark:text-gray-100">{organization.recipient_level}</dd>
                  </div>
                </dl>
              </div>
              <div>
                <dl className="space-y-4">
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</dt>
                    <dd className="text-gray-900 dark:text-gray-100">
                      {organization.metadata?.location
                        ? [
                            organization.metadata.location.address_line1,
                            organization.metadata.location.city_name,
                            organization.metadata.location.state_code,
                            organization.metadata.location.zip,
                          ]
                            .filter(Boolean)
                            .join(', ')
                        : 'N/A'}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400">Business Types</dt>
                    <dd className="flex flex-wrap gap-2">
                      {organization.metadata?.business_types?.map((type, index) => (
                        <span
                          key={index}
                          className="px-2 py-1 text-sm rounded-full bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                        >
                          {type}
                        </span>
                      ))}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>
          </div>

          {/* Funding Summary - All four sections in one row on larger screens */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6 mb-6">
            {/* Awarding Agency Card */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Awarding Agency</h3>
              </div>
              <div className="p-6">
                {organization?.awarding_agency?.results?.map((agency, index) => (
                  <div key={index} className="mb-4 last:mb-0">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {agency?.name} ({agency?.code})
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      Total Amount: {agency?.amount ? formatCurrency(agency.amount) : 'N/A'}
                    </p>
                  </div>
                )) || <p className="text-gray-600 dark:text-gray-400">No awarding agency data available</p>}
              </div>
            </div>

            {/* Subagency Card */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Subagency</h3>
              </div>
              <div className="p-6">
                {organization?.awarding_subagency?.results?.map((subagency, index) => (
                  <div key={index} className="mb-4 last:mb-0">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {subagency?.name} ({subagency?.code})
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      Total Amount: {subagency?.amount ? formatCurrency(subagency.amount) : 'N/A'}
                    </p>
                  </div>
                )) || <p className="text-gray-600 dark:text-gray-400">No subagency data available</p>}
              </div>
            </div>

            {/* Federal Accounts Card - Changed from table to match other cards */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Federal Accounts</h3>
              </div>
              <div className="p-6">
                {organization?.federal_account?.results?.map((account, index) => (
                  <div key={index} className="mb-4 last:mb-0">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {account?.name || 'N/A'} ({account?.code || 'N/A'})
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      Total Amount: {account?.amount ? formatCurrency(account.amount) : 'N/A'}
                    </p>
                  </div>
                )) || <p className="text-gray-600 dark:text-gray-400">No federal account data available</p>}
              </div>
            </div>

            {/* CFDA Programs Card */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">CFDA Programs</h3>
              </div>
              <div className="p-6">
                {organization?.cfda?.results?.map((program, index) => (
                  <div key={index} className="mb-4 last:mb-0">
                    <h4 className="font-medium text-gray-900 dark:text-white">{program?.name || 'N/A'}</h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      Code: {program?.code || 'N/A'}
                      <br />
                      Amount: {program?.amount ? formatCurrency(program.amount) : 'N/A'}
                    </p>
                  </div>
                )) || <p className="text-gray-600 dark:text-gray-400">No CFDA program data available</p>}
              </div>
            </div>
          </div>

          {/* PSC and NAICS Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* PSC Card */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Product Service Codes</h3>
              </div>
              <div className="p-6">
                {organization?.psc?.results?.map((item, index) => (
                  <div key={index} className="mb-4 last:mb-0">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {item?.name || 'N/A'} ({item?.code || 'N/A'})
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      Amount: {item?.amount ? formatCurrency(item.amount) : 'N/A'}
                    </p>
                  </div>
                )) || <p className="text-gray-600 dark:text-gray-400">No PSC data available</p>}
              </div>
            </div>

            {/* NAICS Card */}
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
              <div className="px-6 py-4 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">NAICS Codes</h3>
              </div>
              <div className="p-6">
                {organization?.naics?.results?.map((item, index) => (
                  <div key={index} className="mb-4 last:mb-0">
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      {item?.name || 'N/A'} ({item?.code || 'N/A'})
                    </h4>
                    <p className="text-gray-600 dark:text-gray-400">
                      Amount: {item?.amount ? formatCurrency(item.amount) : 'N/A'}
                    </p>
                  </div>
                )) || <p className="text-gray-600 dark:text-gray-400">No NAICS data available</p>}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Comments Section */}
      <div className="w-[400px] bg-white dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 flex flex-col">
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-lg font-semibold text-gray-800 dark:text-white">Comments</h2>
        </div>
        <Comments commentableType="UsaSpendingRecipient" commentableId={id!} />
      </div>
    </div>
  );
};

export default OrganizationDetails;
